import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import PriceAdvisorModal from "@/components/PriceAdvisorModal";
import { ArrowRight, Car, Motorcycle, CheckCircle, Lightning, Clock, User, Coin } from "@phosphor-icons/react";

const CLASS_ACCENT = {
  B: { bg: "bg-[#fff7ed]", border: "border-orange-200", code_bg: "bg-brand-primary", text: "text-brand-primary", slug: "b-sinifi" },
  "B-Auto": { bg: "bg-[#f0fdf4]", border: "border-green-200", code_bg: "bg-emerald-600", text: "text-emerald-700", slug: "b-otomatik" },
  A1: { bg: "bg-[#fdf4ff]", border: "border-purple-200", code_bg: "bg-purple-600", text: "text-purple-700", slug: "a1" },
  A2: { bg: "bg-[#eff6ff]", border: "border-blue-200", code_bg: "bg-blue-600", text: "text-blue-700", slug: "a2" },
  A: { bg: "bg-[#fff1f2]", border: "border-rose-200", code_bg: "bg-rose-600", text: "text-rose-700", slug: "a" },
  CDE: { bg: "bg-[#f8fafc]", border: "border-slate-200", code_bg: "bg-slate-700", text: "text-slate-700", slug: "c-d-e" },
};

const STEPS = [
  { n: "01", t: "Online Kayıt", d: "5 dakikada TC kimlik ile kayıt" },
  { n: "02", t: "Oryantasyon", d: "İlk gün karşılama ve bilgilendirme" },
  { n: "03", t: "Teorik Ders", d: "34 saat MEB onaylı ders materyali" },
  { n: "04", t: "E-Sınav", d: "Elektronik ortamda teori sınavı" },
  { n: "05", t: "Direksiyon", d: "14 saat uygulamalı sürücü eğitimi" },
  { n: "06", t: "Ehliyet Teslim", d: "Sertifika ve belgeler hazır" },
];

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  useEffect(() => { api.get("/courses").then(r => setCourses(r.data)); }, []);

  function openModal(code = null) {
    setSelectedClass(code);
    setModalOpen(true);
  }

  return (
    <>
      <SEO
        title="Kurslarımız | B, A1, A2, A Sınıfı Ehliyet | Estem Sürücü Kursu"
        description="B (manuel/otomatik), A1, A2, A motosiklet ehliyet kursları. Gebze'de MEB onaylı eğitim, %87 sınav başarısı. Detaylı bilgi ve fiyatlar."
        canonical="https://www.estemsurucukursu.com/kurslarimiz"
      />

      {/* Hero */}
      <section className="px-4 md:px-8 py-12 md:py-20 max-w-7xl mx-auto">
        <div className="nb-badge mb-4">Kurslarımız</div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h1 className="font-heading font-bold text-5xl md:text-7xl tracking-tight leading-[1.05]">
            Tüm sınıflar.<br/>
            <span className="text-brand-primary">Tek çatı altında.</span>
          </h1>
          <p className="text-brand-muted max-w-sm text-base md:text-right">
            B'den A'ya, manueldan otomatiğe. Her sürücü adayı için doğru sınıf burada.
          </p>
        </div>

        {/* Quick stats */}
        <div className="mt-8 flex flex-wrap gap-6 border-t border-b border-stone-200 py-6">
          {[
            { icon: User, v: "33.000+", l: "Mezun" },
            { icon: Lightning, v: "%87", l: "1. Denemede Başarı" },
            { icon: Clock, v: "37 Yıl", l: "Tecrübe" },
            { icon: Car, v: "13 Araç", l: "Aktif Filo" },
          ].map(({ icon: Icon, v, l }) => (
            <div key={l} className="flex items-center gap-3">
              <Icon size={20} weight="duotone" className="text-brand-primary shrink-0"/>
              <span className="font-heading font-bold text-xl">{v}</span>
              <span className="text-stone-500 text-sm">{l}</span>
            </div>
          ))}
        </div>

        {/* Course cards */}
        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          {courses.map((c) => {
            const ac = CLASS_ACCENT[c.code] || CLASS_ACCENT.B;
            const Icon = c.code.startsWith("A") || c.code === "A" ? Motorcycle : Car;
            const slug = ac.slug;
            return (
              <article
                key={c.code}
                className={`rounded-2xl border ${ac.border} ${ac.bg} p-7 md:p-8 flex flex-col gap-5 group transition-all hover:shadow-lg hover:-translate-y-0.5 duration-200`}
                data-testid={`course-detail-${c.code}`}
              >
                {/* Top row */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`${ac.code_bg} text-white rounded-xl px-4 py-2 font-heading font-black text-2xl tracking-tighter leading-none`}>
                      {c.code}
                    </div>
                    {c.popular && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-brand-primary text-white px-2.5 py-1 rounded-full">
                        Popüler
                      </span>
                    )}
                  </div>
                  <Icon size={32} weight="duotone" className={ac.text}/>
                </div>

                {/* Name + desc */}
                <div>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl tracking-tight">{c.name}</h2>
                  <p className="mt-2 text-stone-500 text-sm leading-relaxed">{c.description}</p>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white/80 rounded-xl p-3 text-center border border-stone-100">
                    <div className="font-heading font-bold text-xl">{c.min_age}+</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 mt-0.5">Min. Yaş</div>
                  </div>
                  <button
                    onClick={() => openModal(c.code)}
                    data-testid={`course-price-btn-${c.code}`}
                    className="bg-brand-primary/10 hover:bg-brand-primary/20 rounded-xl p-3 text-center border border-brand-primary/20 hover:border-brand-primary/40 transition-all group"
                  >
                    <div className="font-heading font-bold text-xs text-brand-primary flex items-center justify-center gap-1"><Coin size={12} weight="fill"/>Fiyatları</div>
                    <div className="text-[10px] font-semibold text-brand-primary/70 mt-0.5">Gör</div>
                  </button>
                  <div className="bg-white/80 rounded-xl p-3 text-center border border-stone-100">
                    <div className="font-heading font-bold text-base leading-tight mt-0.5">{c.duration?.split(" ").slice(0, 2).join(" ")}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500 mt-0.5">Süre</div>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-1.5 text-sm">
                  {["MEB onaylı sertifika", "E-sınav hazırlığı dahil", "Tüm materyaller dahil"].map(t => (
                    <li key={t} className="flex items-center gap-2 text-stone-600">
                      <CheckCircle weight="fill" size={15} className="text-emerald-500 shrink-0"/>
                      {t}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Link
                  to={`/kurs/${slug}`}
                  className={`mt-auto w-full flex items-center justify-center gap-2 font-semibold text-sm py-3 rounded-xl transition-all ${ac.code_bg} text-white hover:opacity-90 active:scale-[0.98]`}
                  data-testid={`enroll-${c.code}`}
                >
                  {c.code} Kursunu İncele <ArrowRight weight="bold" size={15} className="group-hover:translate-x-0.5 transition-transform"/>
                </Link>
              </article>
            );
          })}
        </div>

        {/* Process */}
        <div className="mt-16">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-2">Ehliyet Alma Süreci</h2>
          <p className="text-stone-500 mb-8 text-sm">Kayıttan sertifikaya — 6 adımda ne beklediğini bil.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white rounded-2xl border border-stone-200 hover:border-brand-primary/30 transition-colors">
                <div className="font-heading font-black text-3xl text-brand-primary/20 leading-none shrink-0 w-10">{s.n}</div>
                <div>
                  <div className="font-heading font-bold text-base">{s.t}</div>
                  <div className="text-stone-500 text-sm mt-0.5">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 rounded-2xl bg-brand-ink text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-heading font-bold text-3xl md:text-4xl tracking-tight">Sınıfını seçtin mi?</div>
            <p className="mt-2 text-white/70 text-sm">Kayıt 5 dakika sürer. Kontenjanlar doluyor.</p>
          </div>
          <Link to="/basla" className="nb-btn !px-8 !py-4 !text-base shrink-0">
            Hemen Kayıt Ol <ArrowRight weight="bold"/>
          </Link>
        </div>

        {/* Neden Estem */}
        <div className="mt-16">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mb-2">Neden Estem?</h2>
          <p className="text-stone-500 text-sm mb-8">Gebze'de 37 yıldır tercih edilmenin 6 nedeni.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { n: "01", t: "MEB Akreditasyonu", d: "Milli Eğitim Bakanlığı tarafından denetlenen ve onaylanan müfredat. Belgeniz her kurumda geçerli." },
              { n: "02", t: "%87 İlk Deneme Başarısı", d: "Kursiyerlerimizin %87'si teorik ve direksiyon sınavını ilk denemede geçiyor." },
              { n: "03", t: "13 Aktif Araç", d: "Modern eğitim araçları, düzenli bakımlı ve her türlü eğitime hazır. Bekleme süresi minimum." },
              { n: "04", t: "Online Kayıt", d: "Tüm kayıt ve belge süreci dijital. Sıraya girme derdi yok, evden tamamlayın." },
              { n: "05", t: "Kadın Eğitmen", d: "Kadın kursiyerler için kadın eğitmen tercih hakkı. Konforlu ve güvenli öğrenme ortamı." },
              { n: "06", t: "Ücretsiz Takviye", d: "Sınavı geçemeyen kursiyerimize ek ücret olmadan takviye dersi veriyoruz." },
            ].map((s, i) => (
              <div key={i} className="flex gap-4 p-5 bg-white rounded-2xl border border-stone-200 hover:border-brand-primary/30 transition-colors">
                <div className="font-heading font-black text-3xl text-brand-primary/20 leading-none shrink-0 w-10">{s.n}</div>
                <div>
                  <div className="font-heading font-bold text-base">{s.t}</div>
                  <div className="text-stone-500 text-sm mt-1 leading-relaxed">{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gerekli Evraklar Teaser */}
        <div className="mt-10 rounded-2xl border border-brand-primary/20 bg-[#fff7ed] p-7 flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1">
            <div className="font-heading font-bold text-xl">Kayıt için hangi belgeler gerekli?</div>
            <p className="text-stone-500 text-sm mt-1">TC kimlik, öğrenim belgesi, sağlık raporu ve daha fazlası — tam listeye bakın.</p>
          </div>
          <Link to="/kayit-evraklari" className="shrink-0 nb-btn !bg-brand-primary !text-white !px-6 !py-3 !text-sm">Belge Listesi <ArrowRight weight="bold" size={14}/></Link>
        </div>

        {/* SSS Teaser */}
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-7 flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex-1">
            <div className="font-heading font-bold text-xl">Aklınızda soru mu var?</div>
            <p className="text-stone-500 text-sm mt-1">Kurs süresi, fiyatlar, sınav tarihleri hakkında en sık sorulan sorular.</p>
          </div>
          <Link to="/sss" className="shrink-0 nb-btn !bg-brand-ink !text-white !px-6 !py-3 !text-sm">SSS'yi Gör <ArrowRight weight="bold" size={14}/></Link>
        </div>
      </section>

      <PriceAdvisorModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        defaultClass={selectedClass}
      />
    </>
  );
}
